/**
 * LangProvider (#D5)
 *
 * App-wide UI language. English is the base; RU is the translation. Mirrors the
 * ThemeProvider pattern (context + state + localStorage) so a language switch
 * re-renders the UI LIVE — no reload. Persists into the shared 'adhesion-settings'
 * object via read-modify-write so it never clobbers the other settings keys.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { t as translate, type Lang, type StringKey, type TParams } from '../lib/i18n';
import { persistSettings, readSettings } from '../lib/settings';

export type LangSetting = 'auto' | 'en' | 'ru';

function resolveLang(setting: LangSetting): Lang {
  if (setting === 'en' || setting === 'ru') return setting;
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    return navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
  }
  return 'en';
}

function readSetting(): LangSetting {
  const v = readSettings().language;
  return v === 'auto' || v === 'en' || v === 'ru' ? v : 'auto';
}

function persistSetting(setting: LangSetting): void {
  persistSettings({ language: setting });
}

interface LangContextValue {
  setting: LangSetting;
  lang: Lang;
  setLanguage: (setting: LangSetting) => void;
  t: (key: StringKey, params?: TParams) => string;
}

const LangContext = createContext<LangContextValue | undefined>(undefined);

export function LangProvider({ children }: { children: ReactNode }) {
  const [setting, setSetting] = useState<LangSetting>(() => readSetting());
  const lang = resolveLang(setting);

  const setLanguage = useCallback((next: LangSetting) => {
    persistSetting(next);
    setSetting(next);
  }, []);

  const t = useCallback((key: StringKey, params?: TParams) => translate(key, lang, params), [lang]);

  return (
    <LangContext.Provider value={{ setting, lang, setLanguage, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) {
    // Fallback when used outside a provider (e.g. isolated tests): resolve from
    // storage/browser and translate without live switching.
    const fallbackLang = resolveLang(readSetting());
    return {
      setting: readSetting(),
      lang: fallbackLang,
      setLanguage: () => {},
      t: (key: StringKey, params?: TParams) => translate(key, fallbackLang, params),
    };
  }
  return ctx;
}

/** Convenience: just the translate function bound to the active language. */
export function useT(): (key: StringKey, params?: TParams) => string {
  return useLang().t;
}
